import { useState, useEffect } from "react";
import { CreditCard, ExternalLink, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/customer/invoices`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data?.invoices ?? data ?? []);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      toast.error("Failed to load invoices");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (invoice, paymentOption) => {
    try {
      const response = await fetch(`${API}/customer/invoices/${invoice.id}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          payment_option: paymentOption,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create payment session");
      }
    } catch (error) {
      toast.error("Error processing payment");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="text-[#22C55E]" size={20} />;
      case 'overdue':
        return <AlertCircle className="text-[#EF4444]" size={20} />;
      default:
        return <Clock className="text-[#F59E0B]" size={20} />;
    }
  };

  const totalDue = (invoices || []).reduce((sum, i) => {
    const paid = i.amount_paid || (i.status === 'paid' ? i.total : 0) || 0;
    return sum + Math.max(0, (i.total || 0) - paid);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-invoices">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Invoices</h1>
        <p className="text-[#A1A1AA] mt-1">View and pay your invoices</p>
      </div>

      {/* Balance Summary */}
      {totalDue > 0 && (
        <Card className="bg-[#F59E0B]/10 border-[#F59E0B]">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard size={24} className="text-[#F59E0B]" />
                <div>
                  <p className="text-[#FAFAFA] font-medium">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-[#F59E0B]">{formatCurrency(totalDue)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices List */}
      <div className="space-y-4">
        {(invoices || []).map((invoice) => (
          <Card key={invoice.id} className="bg-[#121214] border-[#27272A]" data-testid={`invoice-${invoice.id}`}>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  {getStatusIcon(invoice.status)}
                  <div>
                    <h3 className="font-semibold text-[#FAFAFA]">{invoice.invoice_number}</h3>
                    <p className="text-sm text-[#71717A]">Due: {invoice.due_date}</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="text-left md:text-right">
                    <p className="text-2xl font-bold text-[#D4AF37]">{formatCurrency(invoice.total)}</p>
                    <p className="text-xs text-[#71717A] mt-1">
                      Paid: {formatCurrency(invoice.amount_paid || (invoice.status === 'paid' ? invoice.total : 0) || 0)}
                      {" · "}
                      Remaining: {formatCurrency(Math.max(0, (invoice.total || 0) - (invoice.amount_paid || (invoice.status === 'paid' ? invoice.total : 0) || 0)))}
                    </p>
                    <span className={`badge ${
                      (invoice.payment_status === 'paid' || invoice.status === 'paid') ? 'badge-success' : 
                      invoice.status === 'overdue' ? 'badge-error' : 'badge-warning'
                    }`}>
                      {invoice.payment_status || invoice.status}
                    </span>
                  </div>

                  {(invoice.payment_status !== 'paid' && invoice.status !== 'paid') && (
                    <div className="flex gap-2">
                      {invoice.deposit_amount > 0 && (invoice.amount_paid || 0) < invoice.deposit_amount && (
                        <Button 
                          onClick={() => handlePay(invoice, 'deposit')}
                          className="btn-gold"
                          data-testid={`pay-deposit-${invoice.id}`}
                        >
                          <CreditCard size={16} className="mr-2" />
                          Pay Deposit
                        </Button>
                      )}
                      {(invoice.deposit_amount === 0 || (invoice.amount_paid || 0) >= invoice.deposit_amount) && (
                        <Button 
                          onClick={() => handlePay(invoice, 'balance')}
                          className="btn-gold"
                          data-testid={`pay-balance-${invoice.id}`}
                        >
                          <CreditCard size={16} className="mr-2" />
                          Pay Balance
                        </Button>
                      )}
                    </div>
                  )}

                  {invoice.payment_link && (invoice.payment_status !== 'paid' && invoice.status !== 'paid') && (
                    <Button 
                      variant="outline"
                      onClick={() => window.open(invoice.payment_link, '_blank')}
                      className="border-[#27272A] text-[#A1A1AA]"
                      title="Open latest payment link"
                    >
                      <ExternalLink size={16} />
                    </Button>
                  )}
                </div>
              </div>

              {/* Invoice Items */}
              <div className="mt-4 pt-4 border-t border-[#27272A]">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-[#71717A]">
                      <th className="text-left pb-2">Description</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Price</th>
                      <th className="text-right pb-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items?.map((item, idx) => (
                      <tr key={idx} className="text-sm">
                        <td className="text-[#FAFAFA] py-1">{item.description}</td>
                        <td className="text-[#A1A1AA] text-right">{item.quantity}</td>
                        <td className="text-[#A1A1AA] text-right">{formatCurrency(item.price)}</td>
                        <td className="text-[#FAFAFA] text-right">{formatCurrency(item.quantity * item.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}

        {(invoices || []).length === 0 && (
          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="py-12 text-center">
              <CheckCircle2 size={48} className="text-[#22C55E] mx-auto mb-3" />
              <p className="text-[#FAFAFA] font-medium">All Caught Up!</p>
              <p className="text-[#71717A]">No invoices at this time</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerInvoices;
